"use client";

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { PAGINATION } from "@unreserved/constants";
import type {
  ListingHomeApiResponse,
  PriceEstimateResponseDto,
  PropertyAddressSuggestionsResponseDto,
} from "@unreserved/sdk";
import { listingApi } from "@unreserved/services";

import { useOwnedListingsInfinite as useOwnedListingsInfiniteImpl } from "@/hooks/listing/use-listing-owned";

type ListingsPageParams = {
  pageSize?: number;
};

const DEFAULT_PAGE_SIZE = PAGINATION.DEFAULT_LIMIT;

const getNextPageParam = (lastPage: ListingHomeApiResponse) => {
  const pagination = lastPage.data?.pagination;

  if (!pagination) {
    return undefined;
  }

  if (pagination.pageNo >= pagination.totalPages) {
    return undefined;
  }

  return pagination.pageNo + 1;
};

export function useEnquiredListingsInfinite(options?: ListingsPageParams) {
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;

  return useInfiniteQuery<ListingHomeApiResponse>({
    queryKey: ["listings", "enquired", "infinite", pageSize],
    queryFn: ({ pageParam }) =>
      listingApi.listingControllerEnquireAboutListingV1({
        pageNo: (pageParam as number) ?? 1,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam,
  });
}

/** Re-export from listing/use-listing-owned for backward compatibility */
export { useOwnedListingsInfiniteImpl as useOwnedListingsInfinite };

export function usePropertyAddressSuggestions(keyword: string, enabled: boolean = true) {
  return useQuery<PropertyAddressSuggestionsResponseDto>({
    queryKey: ["listings", "address-suggestions", keyword],
    queryFn: () => listingApi.listingControllerGetPropertyAddressSuggestionsV1({ keyword }),
    enabled: enabled && !!keyword.trim(),
  });
}

export function usePropertyPriceEstimate(propertyId: string, enabled: boolean = true) {
  return useQuery<PriceEstimateResponseDto>({
    queryKey: ["listings", "price-estimate", propertyId],
    queryFn: () => listingApi.listingControllerGetPriceEstimateV1({ id: propertyId }),
    enabled: enabled && !!propertyId,
  });
}
